import { Link } from 'react-router-dom'
import { Card, CardHeader, SettingRow } from '../../ds'

/**
 * The fixed floor — read-only (MAR-385, v6 Limits-5).
 *
 * `settings-consolidation.md` §1a: quiet hours, suppression, opt-out, consent and spend caps "are
 * never grantable at any rung and are not a preference. They stay, and they say so in the UI."
 * This is where they say so. There is no control here on purpose: a merchant sets boundaries on
 * the other Limits sections; these are the ones under all of them.
 *
 * ⚠️ Every sentence below names a rule in code. Change one only with the code it describes:
 *  - 9pm–8am for texts: `SMS_QUIET_HOURS_FLOOR` (`@marva/shared/quiet-window`), UNIONED with the
 *    merchant's window by `checkCanSendSms` — never clamped, so no setting opens it.
 *  - The immediate-reply exemption: `IMMEDIATE_REPLY_WINDOW_MS` (15 minutes from the shopper's own
 *    inbound), exempt from the window and the floor alike (MAR-159 review, D-12; pending counsel).
 *    It moved here from the SMS card, which states only what the merchant can change.
 *  - A reply that is not immediate obeys quiet hours, and what happens then depends on WHERE it
 *    is caught. sms-reply-handler's pre-generation preflight (`preflightReply`) holds it before
 *    anything is written: recorded `withheld_compliance` on the Ledger, nothing queued. But a reply
 *    that passed the preflight and then reaches sms-sender outside the 15 minutes (a slow
 *    generation, a retry) meets `checkCanSendSms`'s `rescheduledFor`, and sms-sender RESCHEDULES
 *    it to the end of quiet hours (`rescheduleMessage`; the reply record says `handed_off`). So
 *    "not sent later" is not true today. Whether a late reply should be held rather than deferred
 *    is MAR-393 — change this copy with that fix.
 *  - The timezone: `getCustomerTimezone` — the customer's record, address, phone number or the
 *    device timezone the decision carried; otherwise the store's (engagement rules / workspace);
 *    otherwise America/New_York. Hence "where we know it, otherwise your store's".
 *  - Postal address + unsubscribe: shopper-email's `resolveComplianceFloor` (MAR-330) HOLDS a
 *    proactive email missing either; the address is `workspace.postalAddress`, a Store fact.
 *  - Suppression / opt-out: STOP and its synonyms add the number to the suppression list
 *    (sms-webhook-handler), which proactive texts AND replies check (`SMS_SEND_KINDS`), failing
 *    CLOSED (`getSuppressionList` throws → skipped). An email unsubscribe (unsubscribe-handler) or
 *    spam complaint (complaint-handler) adds the address to the email suppression list
 *    shopper-email checks before a proactive send. The copy deliberately does NOT promise email
 *    REPLIES stop: email-reply-handler's `isEmailSuppressed` fails OPEN on a failed read, so an
 *    opted-out address can still get an AI reply then (MAR-394). Widen the sentence with that fix.
 *  - Consent: a proactive text runs the `consent` check (`smsMarketingConsent.state ===
 *    'subscribed'`); a reply does not, because answering is not marketing.
 *  - Caps over rungs: authority runs after the checks that hold these limits (§1a), so no rung or
 *    grant reaches past a cap, and the discount ceiling clamps what is offered.
 */
const FLOOR: { title: string; detail: React.ReactNode }[] = [
  {
    title: 'Never starts a text between 9pm and 8am',
    detail:
      "In the customer's local time where we know it, otherwise your store's — whatever quiet hours you set, because that is the window US telemarketing rules protect.",
  },
  {
    title: "Answers a customer's text straight away, whatever the hour",
    detail:
      'A reply within 15 minutes of their message goes out even in quiet hours — that is an answer, not a sales message.',
  },
  {
    title: "A reply that can't go out straight away obeys quiet hours",
    detail:
      "In quiet hours, a text she can't answer within 15 minutes gets no reply, and your Ledger shows it as held. A reply she has already written that misses the 15 minutes waits for quiet hours to end, then goes out.",
  },
  {
    title: 'Every email she starts carries your postal address and an unsubscribe link',
    detail: (
      <>
        Commercial-email rules. An email missing either is held, not sent. The address is set on{' '}
        <Link to="/authority?tab=store&section=address">Store</Link>.
      </>
    ),
  },
  {
    title: 'Someone who opts out gets nothing she starts on that channel',
    detail:
      'Unsubscribing or reporting an email as spam stops the emails she starts. Texting STOP stops the texts she starts and her replies to that number.',
  },
  {
    title: 'She texts a customer first only with their consent',
    detail: 'They must have agreed to marketing texts. Answering a text they sent does not need it.',
  },
  {
    title: 'No rung lifts a limit you set',
    detail:
      'What you allow in Authority decides what she may do inside these limits. The discount ceiling, the budget and the message caps hold at every rung.',
  },
]

const FixedFloorSection = () => (
  <Card data-testid="fixed-floor">
    <CardHeader
      titleAs="h2"
      title="The fixed floor"
      description="At every rung, whatever you set. Nothing here can be changed."
    />
    <div>
      {FLOOR.map((rule) => (
        <SettingRow key={rule.title} title={rule.title} description={rule.detail} />
      ))}
    </div>
  </Card>
)

export default FixedFloorSection
